import { AppDispatch } from "redux/store";
import { CallLambda } from "./CallLambda";
import Session from "redux/classes/Session";

/**
 * Stage a job's config JSON in S3 ahead of jobCreator.
 *
 * Why this exists: prompts and other "textbox large" parameters can easily push
 * the inline jobCreator request body past the AWS WAF body-inspection threshold
 * (default 8 KB on the standard tier), which results in a CloudFront 403 before
 * the request ever reaches API Gateway. By uploading the config to S3 first and
 * passing only the S3 key to jobCreator, the body stays small regardless of
 * how much the user has written into their parameters.
 *
 * Mirrors the established `fileUploader -> presigned PUT -> reference key in
 * the next request` pattern that input files already use (see UploadInputAction).
 *
 * Returns the S3 key (relative to the model bucket, e.g.
 * `<modelAlias>/config/<jobID>.json`) for jobCreator's `configS3Path` field.
 */
export function UploadConfigAction(session: Session, modelAlias: string, jobID: string, configJSON: string) {
    return async (dispatch: AppDispatch): Promise<string> => {
        // fileUploader expects a relative file name (under the chosen target);
        // it composes the full S3 key as `<modelAlias>/<target>/<file>`.
        // Use the jobID as the filename so collisions are impossible without
        // a clock collision, and the resulting S3 key matches jobCreator's
        // `<modelAlias>/config/<jobID>.json` lookup convention.
        const fileName = `${jobID}.json`;
        const fileSize = new Blob([configJSON]).size.toString();

        const payload = JSON.stringify({
            "model": modelAlias,
            "file": fileName,
            "size": fileSize,
            "type": "application/json",
            "target": "config",
        });

        const response: { status: number, data: any } = await dispatch(CallLambda(session, "fileUploader", payload));

        if (response.status !== 200 || !response.data?.presignedURL) {
            throw new Error("Failed to stage job config");
        }

        const uploadResponse = await fetch(response.data.presignedURL, {
            method: 'PUT',
            body: configJSON,
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (!uploadResponse.ok) {
            throw new Error("Failed to stage job config");
        }

        // jobCreator strips a leading slash and prepends `<modelAlias>/`, so
        // the path it expects is the file's location relative to the model
        // root: `config/<fileName>`.
        return `config/${fileName}`;
    };
}
